import React, { ComponentType } from 'react';
import useFeatureFlags from './useFeatureFlags';
import { FeatureFlagKeys } from 'src/types/featureFlags';

export interface PermissionsAccessControl {
  /** Feature flags that must ALL be enabled for the component to render */
  flags: FeatureFlagKeys[];
  /** Optional fallback component to render when flags are disabled */
  fallback?: React.ReactNode;
}

export const PermissionsAccessControl: React.FC<React.PropsWithChildren<PermissionsAccessControl>> = (props) => {
  const { children, flags, fallback } = props
  
  const featureFlags = useFeatureFlags();
  const allFlagsEnabled = flags.every((flag) => !!featureFlags[flag]);

  if (!allFlagsEnabled) {
    return fallback as React.ReactElement;
  }
  
  return children as React.ReactElement
}

/**
 * HOC that conditionally renders a component based on user permissions.
 * All specified flags must be enabled for the wrapped component to render.
 *
 * @example
 * // Basic usage - component only renders if BEAC_6655_custom_deck_usage is true
 * const ProtectedButton = withPermissionsAccessControl(MyButton, {
 *   flags: ['BEAC_6655_custom_deck_usage'],
 * });
 *
 * @example
 * // Multiple flags - ALL must be enabled
 * const ProtectedFeature = withPermissionsAccessControl(MyFeature, {
 *   flags: ['BEAC_6655_custom_deck_usage', 'enableEditSharedFolder'],
 * });
 *
 * @example
 * // With fallback component
 * const ProtectedFeature = withPermissionsAccessControl(MyFeature, {
 *   flags: ['BEAC_6655_custom_deck_usage'],
 *   fallback: <DisabledMessage />,
 * });
 */
function withPermissionsAccessControl<P extends object>(
  WrappedComponent: ComponentType<P>,
  options: PermissionsAccessControl,
): React.FC<P> {
  const withPermissionsAccessControlComponent: React.FC<P> = (props) => {
    return (
      <PermissionsAccessControl {...options}>
        <WrappedComponent {...props} />
      </PermissionsAccessControl>
    )
  };

  return withPermissionsAccessControlComponent;
}

export default withPermissionsAccessControl;
